import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
    Box,
    Typography,
    Paper,
    Grid,
    useMediaQuery,
    useTheme,
    Autocomplete,
    Checkbox,
    TextField,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    Button,
    CircularProgress,
    FormControlLabel,
    Switch,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Table,
    TableHead,
    TableRow,
    TableCell,
    TableBody,
    TablePagination,
} from '@mui/material';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutlineOutlined';
import CancelOutlinedIcon from '@mui/icons-material/CancelOutlined';
import SpeedIcon from '@mui/icons-material/Speed';
import {
    ResponsiveContainer,
    BarChart,
    Bar,
    XAxis,
    YAxis,
    CartesianGrid,
    Tooltip,
    Legend,
} from 'recharts';
import moment from 'moment';
import html2canvas from 'html2canvas';
import jsPDF from 'jspdf';
import Layout from './Layout';
import {
    getClientsName,
    getOtsDotMetrics,
    getOperationalDispatchDebug,
    getOperationalNotOnTimeDetails,
} from '../services/api';
import { useSnackbar } from '../contexts/SnackbarContext';

const dateRangePresetOptions = [
    { value: 'LAST_YEAR_TO_YESTERDAY', label: 'Last year (1/1) to yesterday' },
    { value: 'TODAY', label: 'Today' },
    { value: 'YESTERDAY', label: 'Yesterday' },
    { value: 'LAST_7_DAYS', label: 'Last 7 days' },
    { value: 'LAST_30_DAYS', label: 'Last 30 days' },
    { value: 'LAST_90_DAYS', label: 'Last 90 days' },
    { value: 'THIS_MONTH', label: 'This month (MTD)' },
    { value: 'LAST_MONTH', label: 'Last month' },
    { value: 'LAST_12_MONTHS', label: 'Last 12 months' },
    { value: 'THIS_YEAR', label: 'This year (YTD)' },
    { value: 'CUSTOM', label: 'Custom range' },
];

const getDateRangeFromPreset = (preset) => {
    const today = moment().startOf('day');
    const yesterday = today.clone().subtract(1, 'day');
    switch (preset) {
        case 'LAST_YEAR_TO_YESTERDAY':
            return {
                startDate: today.clone().subtract(1, 'year').startOf('year').format('YYYY-MM-DD'),
                endDate: yesterday.format('YYYY-MM-DD'),
            };
        case 'TODAY':
            return {
                startDate: today.format('YYYY-MM-DD'),
                endDate: today.format('YYYY-MM-DD'),
            };
        case 'YESTERDAY':
            return {
                startDate: yesterday.format('YYYY-MM-DD'),
                endDate: yesterday.format('YYYY-MM-DD'),
            };
        case 'LAST_7_DAYS':
            return {
                startDate: today.clone().subtract(6, 'days').format('YYYY-MM-DD'),
                endDate: today.format('YYYY-MM-DD'),
            };
        case 'LAST_30_DAYS':
            return {
                startDate: today.clone().subtract(29, 'days').format('YYYY-MM-DD'),
                endDate: today.format('YYYY-MM-DD'),
            };
        case 'LAST_90_DAYS':
            return {
                startDate: today.clone().subtract(89, 'days').format('YYYY-MM-DD'),
                endDate: today.format('YYYY-MM-DD'),
            };
        case 'THIS_MONTH':
            return {
                startDate: today.clone().startOf('month').format('YYYY-MM-DD'),
                endDate: today.format('YYYY-MM-DD'),
            };
        case 'LAST_MONTH': {
            const lastMonth = today.clone().subtract(1, 'month');
            return {
                startDate: lastMonth.clone().startOf('month').format('YYYY-MM-DD'),
                endDate: lastMonth.clone().endOf('month').format('YYYY-MM-DD'),
            };
        }
        case 'LAST_12_MONTHS':
            return {
                startDate: today.clone().subtract(12, 'months').startOf('month').format('YYYY-MM-DD'),
                endDate: today.clone().subtract(1, 'months').endOf('month').format('YYYY-MM-DD'),
            };
        case 'THIS_YEAR':
            return {
                startDate: today.clone().startOf('year').format('YYYY-MM-DD'),
                endDate: today.format('YYYY-MM-DD'),
            };
        default:
            return null;
    }
};

const normalizeClientOptions = (clients) => {
    if (!Array.isArray(clients)) return [];

    const unique = new Set();
    const options = [];
    clients.forEach((item) => {
        let clientName = null;
        if (typeof item === 'string') {
            clientName = item.trim();
        } else if (item && typeof item === 'object') {
            if (typeof item.clientName === 'string') clientName = item.clientName.trim();
            else if (typeof item.name === 'string') clientName = item.name.trim();
        }
        if (!clientName || unique.has(clientName)) return;
        unique.add(clientName);
        options.push({ value: clientName, label: clientName });
    });

    return options.sort((a, b) => a.label.localeCompare(b.label));
};

const parseClientFilter = (clientNameValue) => {
    const raw = String(clientNameValue || 'ALL').trim();
    if (!raw || raw.toUpperCase() === 'ALL') return [];
    return Array.from(
        new Set(
            raw
                .split('|')
                .map((item) => item.trim())
                .filter(Boolean)
        )
    );
};

const serializeClientFilter = (selectedValues) => {
    const cleaned = Array.from(
        new Set(
            (Array.isArray(selectedValues) ? selectedValues : [])
                .map((item) => String(item || '').trim())
                .filter(Boolean)
                .filter((item) => item.toUpperCase() !== 'ALL')
        )
    );
    return cleaned.length ? cleaned.join('|') : 'ALL';
};

const fmtDateTime = (value) => (value ? moment(value).format('YYYY-MM-DD HH:mm') : '-');
const fmtDateOnly = (value) => (value ? moment(value).format('YYYY-MM-DD') : '-');
const isDateTimeColumn = (col) => {
    const c = (col || '').toLowerCase();
    return ['createdat', 'created_at', 'dispatcheddate', 'dispatched_date', 'requireddispatchday'].includes(c)
        || (c.includes('date') && c !== 'cutofftime');
};
const fmtTimeOnly = (value) => {
    if (value === null || value === undefined || value === '') return '-';

    if (typeof value === 'string') {
        const raw = value.trim();
        const plainTimeMatch = raw.match(/^(\d{2}:\d{2})(?::\d{2}(?:\.\d+)?)?$/);
        if (plainTimeMatch) return plainTimeMatch[1];

        const isoTimeMatch = raw.match(/T(\d{2}:\d{2})/);
        if (isoTimeMatch) return isoTimeMatch[1];
    }

    const parsed = moment(value);
    return parsed.isValid() ? parsed.format('HH:mm') : String(value);
};

const toReadableLabel = (value) => String(value || '')
    .replace(/([A-Z])/g, ' $1')
    .replace(/^./, (s) => s.toUpperCase())
    .trim();

const stickyDebugColumns = ['orderId', 'orderNumber'];
const lateDetailsRowsPerPageOptions = [10, 20, 50, 100, 250, 500];

const OtsDotReport = () => {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
    const initialRange = getDateRangeFromPreset('LAST_YEAR_TO_YESTERDAY') || {
        startDate: moment().subtract(1, 'year').startOf('year').format('YYYY-MM-DD'),
        endDate: moment().subtract(1, 'day').format('YYYY-MM-DD'),
    };

    const [filters, setFilters] = useState({
        startDate: initialRange.startDate,
        endDate: initialRange.endDate,
        dateRangePreset: 'LAST_YEAR_TO_YESTERDAY',
        clientName: 'ALL',
        workingDayStart: '07:00',
        workingDayEnd: '17:00',
        includeOnHoldOrders: true,
        standardSlaDays: 2,
    });
    const [loading, setLoading] = useState(false);
    const [dispatchDebugModalOpen, setDispatchDebugModalOpen] = useState(false);
    const [dispatchDebugLoading, setDispatchDebugLoading] = useState(false);
    const [dispatchDebugData, setDispatchDebugData] = useState(null);
    const [lateDetailsModalOpen, setLateDetailsModalOpen] = useState(false);
    const [lateDetailsLoading, setLateDetailsLoading] = useState(false);
    const [lateDetailsData, setLateDetailsData] = useState(null);
    const [selectedLateClientName, setSelectedLateClientName] = useState('');
    const [lateDetailsPage, setLateDetailsPage] = useState(0);
    const [lateDetailsRowsPerPage, setLateDetailsRowsPerPage] = useState(20);
    const [pdfExporting, setPdfExporting] = useState(false);
    const [clients, setClients] = useState([]);
    const [metrics, setMetrics] = useState({
        dispatchOnTime: { daily: [], dailyByClient: [], monthly: [] },
    });
    const requestIdRef = useRef(0);
    const exportChartRef = useRef(null);
    const { showSnackbar } = useSnackbar();

    useEffect(() => {
        const fetchClients = async () => {
            try {
                const response = await getClientsName();
                if (response?.success && Array.isArray(response.clients)) {
                    setClients(normalizeClientOptions(response.clients));
                }
            } catch (error) {
                console.error('Error fetching clients list:', error);
            }
        };
        fetchClients();
    }, []);

    useEffect(() => {
        const { dateRangePreset, ...apiFilters } = filters;
        const start = moment(apiFilters.startDate, 'YYYY-MM-DD', true);
        const end = moment(apiFilters.endDate, 'YYYY-MM-DD', true);
        const parsedSlaDays = Number.parseInt(String(apiFilters.standardSlaDays), 10);

        if (!start.isValid() || !end.isValid() || start.isAfter(end)) return;
        if (!/^\d{2}:\d{2}$/.test(apiFilters.workingDayStart) || !/^\d{2}:\d{2}$/.test(apiFilters.workingDayEnd)) return;
        if (!Number.isFinite(parsedSlaDays) || parsedSlaDays < 0 || parsedSlaDays > 30) return;
        const requestId = ++requestIdRef.current;
        const debounceId = setTimeout(async () => {
            setLoading(true);
            try {
                const response = await getOtsDotMetrics({
                    ...apiFilters,
                    trendGranularity: 'MONTH',
                });
                if (requestId !== requestIdRef.current) return;

                if (response?.success) {
                    setMetrics({
                        dispatchOnTime: response.dispatchOnTime || { daily: [], dailyByClient: [], monthly: [] },
                    });
                } else {
                    showSnackbar('Failed to load OTS DOT metrics.', 'error');
                }
            } catch (error) {
                if (requestId !== requestIdRef.current) return;
                const backendCode = error?.response?.data?.code;
                const backendMessage = error?.response?.data?.message;
                const isTimeoutWarning = backendCode === 'OPERATIONAL_METRICS_TIMEOUT';
                showSnackbar(
                    backendMessage || 'Failed to load OTS DOT metrics.',
                    isTimeoutWarning ? 'warning' : 'error'
                );
                console.error('Error fetching OTS DOT metrics:', error);
            } finally {
                if (requestId === requestIdRef.current) setLoading(false);
            }
        }, 350);

        return () => clearTimeout(debounceId);
    }, [filters, showSnackbar]);

    const selectedClientNames = useMemo(() => parseClientFilter(filters.clientName), [filters.clientName]);
    const selectedClientOptions = useMemo(
        () => clients.filter((client) => selectedClientNames.includes(client.value)),
        [clients, selectedClientNames]
    );
    const canOpenDispatchDebug = selectedClientNames.length === 1;
    const selectedDebugClientName = canOpenDispatchDebug ? selectedClientNames[0] : '';

    const handleClientSelectionChange = (event, selectedOptions) => {
        const nextClientNames = Array.isArray(selectedOptions)
            ? selectedOptions.map((option) => option.value)
            : [];
        setFilters((prev) => ({
            ...prev,
            clientName: serializeClientFilter(nextClientNames),
        }));
    };

    const handleClearAllClients = () => {
        setFilters((prev) => ({
            ...prev,
            clientName: 'ALL',
        }));
    };

    const handleSelectAllClients = () => {
        setFilters((prev) => ({
            ...prev,
            clientName: serializeClientFilter(clients.map((client) => client.value)),
        }));
    };

    const handleOpenDispatchDebug = async () => {
        if (!canOpenDispatchDebug) {
            showSnackbar('Please select a specific client to run DOT Debug.', 'warning');
            return;
        }
        setDispatchDebugModalOpen(true);
        setDispatchDebugLoading(true);
        setDispatchDebugData(null);

        try {
            const response = await getOperationalDispatchDebug({
                date: filters.endDate,
                clientName: selectedDebugClientName,
                includeOnHoldOrders: filters.includeOnHoldOrders,
                standardSlaDays: filters.standardSlaDays,
                workingDayEnd: filters.workingDayEnd,
            });
            if (response?.success === false) {
                showSnackbar(response?.message || 'DOT debug failed.', 'error');
                return;
            }
            setDispatchDebugData(response || {});
        } catch (error) {
            showSnackbar('Failed to run DOT debug.', 'error');
            console.error('Error loading DOT debug:', error);
        } finally {
            setDispatchDebugLoading(false);
        }
    };

    const handleCloseDispatchDebug = () => setDispatchDebugModalOpen(false);

    const handleOpenLateDetails = async () => {
        setLateDetailsModalOpen(true);
        setLateDetailsLoading(true);
        setLateDetailsData(null);
        setSelectedLateClientName('');
        setLateDetailsPage(0);
        try {
            const response = await getOperationalNotOnTimeDetails({
                startDate: filters.startDate,
                endDate: filters.endDate,
                clientName: filters.clientName,
                includeOnHoldOrders: filters.includeOnHoldOrders,
                standardSlaDays: filters.standardSlaDays,
                workingDayEnd: filters.workingDayEnd,
                skipShippingPriorityFilter: true,
                maxRows: 3000,
            });
            if (response?.success === false) {
                showSnackbar(response?.message || 'Failed to load not-on-time order details.', 'error');
                return;
            }
            setLateDetailsData(response || {});
        } catch (error) {
            const backendMessage = error?.response?.data?.message;
            showSnackbar(backendMessage || 'Failed to load not-on-time order details.', 'error');
            console.error('Error loading not-on-time order details:', error);
        } finally {
            setLateDetailsLoading(false);
        }
    };

    const handleCloseLateDetails = () => {
        setLateDetailsModalOpen(false);
        setSelectedLateClientName('');
        setLateDetailsPage(0);
    };

    const handleLateDetailsPageChange = (event, newPage) => {
        setLateDetailsPage(newPage);
    };

    const handleLateDetailsRowsPerPageChange = (event) => {
        const nextSize = Number.parseInt(event.target.value, 10);
        setLateDetailsRowsPerPage(Number.isFinite(nextSize) && nextSize > 0 ? nextSize : 20);
        setLateDetailsPage(0);
    };

    const handleExportPdf = async () => {
        if (!exportChartRef.current) {
            showSnackbar('Export content is not ready yet.', 'warning');
            return;
        }
        setPdfExporting(true);
        try {
            const pdf = new jsPDF({ orientation: 'landscape', unit: 'mm', format: 'a4' });
            const pageWidth = pdf.internal.pageSize.getWidth();
            const pageHeight = pdf.internal.pageSize.getHeight();
            const margin = 10;
            const contentWidth = pageWidth - margin * 2;
            const headerTop = 12;
            const headerBottom = 34;
            const clientLabel = selectedClientNames.length ? selectedClientNames.join(', ') : 'All Clients';

            const drawHeader = () => {
                pdf.setFont('helvetica', 'bold');
                pdf.setFontSize(15);
                pdf.text('OTS DOT Report', margin, headerTop);
                pdf.setFont('helvetica', 'normal');
                pdf.setFontSize(10);
                pdf.text(`Date Range: ${filters.startDate} to ${filters.endDate}`, margin, headerTop + 7);
                pdf.text(`Client: ${clientLabel}`, margin, headerTop + 12);
                pdf.text(`Generated: ${moment().format('YYYY-MM-DD HH:mm')}`, margin, headerTop + 17);
            };

            drawHeader();

            const canvas = await html2canvas(exportChartRef.current, {
                scale: 2,
                useCORS: true,
                backgroundColor: '#ffffff',
            });
            const pageContentHeightMm = pageHeight - headerBottom - margin;
            const imageAspect = canvas.width / canvas.height;
            let renderWidth = contentWidth;
            let renderHeight = renderWidth / imageAspect;
            if (renderHeight > pageContentHeightMm) {
                renderHeight = pageContentHeightMm;
                renderWidth = renderHeight * imageAspect;
            }
            const x = margin + ((contentWidth - renderWidth) / 2);
            pdf.addImage(canvas.toDataURL('image/png'), 'PNG', x, headerBottom, renderWidth, renderHeight);

            const safeClientName = String(clientLabel).replace(/[^a-zA-Z0-9-_]+/g, '_');
            pdf.save(`ots_dot_report_${filters.startDate}_${filters.endDate}_${safeClientName}.pdf`);
            showSnackbar('PDF exported successfully.', 'success');
        } catch (error) {
            console.error('Error exporting OTS DOT PDF:', error);
            showSnackbar('Failed to export PDF.', 'error');
        } finally {
            setPdfExporting(false);
        }
    };

    const handleExportCsv = () => {
        const rows = monthlySummaryRows;
        if (!rows.length) {
            showSnackbar('No data to export.', 'warning');
            return;
        }
        const totalOrders = rows.reduce((s, r) => s + r.totalOrders, 0);
        const totalOnTime = rows.reduce((s, r) => s + r.onTimeOrders, 0);
        const totalNotOnTime = rows.reduce((s, r) => s + r.notOnTimeOrders, 0);
        const totalOnTimePct = totalOrders > 0 ? Number(((totalOnTime / totalOrders) * 100).toFixed(2)) : 0;
        const totalNotOnTimePct = totalOrders > 0 ? Number(((totalNotOnTime / totalOrders) * 100).toFixed(2)) : 0;
        const escapeCsv = (x) => `"${String(x).replace(/"/g, '""')}"`;
        const header = ['Month', 'Total Orders', 'On-Time Orders', 'On-Time %', 'Not On-Time Orders', 'Not On-Time %'];
        const dataRows = rows.map((r) => [
            r.month,
            r.totalOrders.toLocaleString(),
            r.onTimeOrders.toLocaleString(),
            `${r.onTimePct.toFixed(2)}%`,
            r.notOnTimeOrders.toLocaleString(),
            `${r.notOnTimePct.toFixed(2)}%`,
        ]);
        const totalRow = ['Total', totalOrders.toLocaleString(), totalOnTime.toLocaleString(), `${totalOnTimePct.toFixed(2)}%`, totalNotOnTime.toLocaleString(), `${totalNotOnTimePct.toFixed(2)}%`];
        const csv = [header, ...dataRows, totalRow].map((r) => r.map(escapeCsv).join(',')).join('\r\n');
        const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `ots_dot_monthly_${filters.startDate}_${filters.endDate}.csv`;
        a.click();
        window.URL.revokeObjectURL(url);
        showSnackbar('CSV exported successfully.', 'success');
    };

    const dispatchMonthlyChart = useMemo(
        () =>
            (metrics.dispatchOnTime.monthly || []).map((row) => {
                const totalEligibleOrders = Number(row.expressEligibleOrders || 0) + Number(row.standardEligibleOrders || 0);
                const totalOnTimeOrders = Number(row.expressOnTimeOrders || 0) + Number(row.standardOnTimeOrders || 0);
                const totalLateOrders = Math.max(totalEligibleOrders - totalOnTimeOrders, 0);
                const expressOnTimePct = Number(row.expressOnTimePct ?? 100);
                const standardOnTimePct = Number(row.standardOnTimePct ?? 100);
                return {
                    label: moment(row.periodMonth).format('MMM YYYY'),
                    totalEligibleOrders,
                    totalOnTimeOrders,
                    totalLateOrders,
                    totalOnTimePct: totalEligibleOrders === 0
                        ? 100
                        : Number(((totalOnTimeOrders * 100) / totalEligibleOrders).toFixed(2)),
                    expressOnTimePct,
                    standardOnTimePct,
                };
            }),
        [metrics.dispatchOnTime.monthly]
    );

    const trendChartData = dispatchMonthlyChart;
    const latestTrendPoint = trendChartData[trendChartData.length - 1];
    const latestPeriodLabel = latestTrendPoint?.label || 'No period';
    const renderStackTopLabel = (props, mode) => {
        const {
            x, y, width, index,
        } = props;
        if (
            typeof x !== 'number'
            || typeof y !== 'number'
            || typeof width !== 'number'
            || typeof index !== 'number'
        ) {
            return null;
        }
        const point = trendChartData[index];
        if (!point) return null;
        if (mode === 'late') {
            const shouldRender = Number(point.totalLateOrders || 0) > 0;
            if (!shouldRender) return null;
            return (
                <text x={x + (width / 2)} y={y - 6} fill="#1f2937" textAnchor="middle" fontSize={11} fontWeight={600}>
                    {`${Number(point.totalEligibleOrders || 0).toLocaleString()} total`}
                </text>
            );
        }
        const shouldRender = Number(point.totalLateOrders || 0) === 0;
        if (!shouldRender) return null;
        return (
            <text x={x + (width / 2)} y={y - 6} fill="#1f2937" textAnchor="middle" fontSize={11} fontWeight={600}>
                {`${Number(point.totalEligibleOrders || 0).toLocaleString()} total`}
            </text>
        );
    };
    const latestNotOnTimePct = latestTrendPoint
        ? Number((100 - Number(latestTrendPoint.totalOnTimePct || 0)).toFixed(2))
        : 0;
    const latestNotOnTimeOrders = latestTrendPoint ? Number(latestTrendPoint.totalLateOrders || 0) : 0;
    const latestOnTimeOrders = latestTrendPoint ? Number(latestTrendPoint.totalOnTimeOrders || 0) : 0;
    const latestOnTimePct = latestTrendPoint ? Number(latestTrendPoint.totalOnTimePct || 0) : 0;

    const avgMonthlyExpressPct = useMemo(() => {
        if (!trendChartData.length) return null;
        const sum = trendChartData.reduce((acc, r) => acc + Number(r.expressOnTimePct ?? 100), 0);
        return Number((sum / trendChartData.length).toFixed(2));
    }, [trendChartData]);
    const avgMonthlyStandardPct = useMemo(() => {
        if (!trendChartData.length) return null;
        const sum = trendChartData.reduce((acc, r) => acc + Number(r.standardOnTimePct ?? 100), 0);
        return Number((sum / trendChartData.length).toFixed(2));
    }, [trendChartData]);
    const avgMonthlyOtsDotPct = useMemo(() => {
        if (!trendChartData.length) return null;
        const sum = trendChartData.reduce((acc, r) => acc + Number(r.totalOnTimePct ?? 100), 0);
        return Number((sum / trendChartData.length).toFixed(2));
    }, [trendChartData]);
    const monthlySummaryRows = useMemo(
        () =>
            trendChartData.map((row) => ({
                month: row.label,
                totalOrders: Number(row.totalEligibleOrders || 0),
                onTimeOrders: Number(row.totalOnTimeOrders || 0),
                onTimePct: Number(row.totalOnTimePct || 0),
                notOnTimeOrders: Number(row.totalLateOrders || 0),
                notOnTimePct: Number((100 - Number(row.totalOnTimePct || 0)).toFixed(2)),
            })),
        [trendChartData]
    );

    const dispatchDebugSummary = useMemo(
        () => (dispatchDebugData?.summary && typeof dispatchDebugData.summary === 'object'
            ? dispatchDebugData.summary
            : null),
        [dispatchDebugData]
    );
    const dispatchDebugRows = useMemo(
        () => (Array.isArray(dispatchDebugData?.contributingOrders) ? dispatchDebugData.contributingOrders : []),
        [dispatchDebugData]
    );
    const dispatchDebugColumns = useMemo(() => {
        if (!dispatchDebugRows.length) return [];
        const preferredOrder = [
            'orderId',
            'orderNumber',
            'orderNo',
            'clientName',
            'shippingMethod',
            'shippingPriority',
            'createdDate',
            'dispatchedDate',
            'baseOrderDay',
            'requiredDispatchDay',
            'isCancelled',
            'hasIssueStatus',
            'isIssueStatusExcluded',
            'isExpressOrder',
            'expressEligible',
            'expressOnTime',
            'standardEligible',
            'standardOnTime',
        ];
        const allKeys = Array.from(
            dispatchDebugRows.reduce((set, row) => {
                Object.keys(row || {}).forEach((key) => set.add(key));
                return set;
            }, new Set())
        );
        const ordered = preferredOrder.filter((key) => allKeys.includes(key));
        const extras = allKeys.filter((key) => !ordered.includes(key));
        return [...ordered, ...extras];
    }, [dispatchDebugRows]);
    const debugStickyLeftByColumn = useMemo(() => {
        const widths = { orderId: 110, orderNumber: 160 };
        let runningLeft = 0;
        return dispatchDebugColumns.reduce((acc, column) => {
            if (!stickyDebugColumns.includes(column)) return acc;
            acc[column] = runningLeft;
            runningLeft += widths[column] || 140;
            return acc;
        }, {});
    }, [dispatchDebugColumns]);
    const contributingOrderCount = Number(dispatchDebugSummary?.contributingOrders ?? dispatchDebugRows.length ?? 0);
    const dispatchDebugSummaryItems = useMemo(() => {
        if (!dispatchDebugSummary) return [];
        const preferredOrder = [
            'expressEligibleOrders',
            'expressOnTimeOrders',
            'standardEligibleOrders',
            'standardOnTimeOrders',
            'contributingOrders',
        ];
        const keys = Object.keys(dispatchDebugSummary);
        const ordered = preferredOrder.filter((key) => keys.includes(key));
        const extras = keys.filter((key) => !ordered.includes(key) && !['expressOnTimePct', 'standardOnTimePct'].includes(key));
        return [...ordered, ...extras].map((key) => {
            if (key === 'expressOnTimeOrders') {
                const count = Number(dispatchDebugSummary.expressOnTimeOrders || 0);
                const pctValue = Number(dispatchDebugSummary.expressOnTimePct || 0).toFixed(2);
                return { key, label: 'Express On Time Orders (DOT%)', value: `${count} (${pctValue}%)` };
            }
            if (key === 'standardOnTimeOrders') {
                const count = Number(dispatchDebugSummary.standardOnTimeOrders || 0);
                const pctValue = Number(dispatchDebugSummary.standardOnTimePct || 0).toFixed(2);
                return { key, label: 'Standard On Time Orders (DOT%)', value: `${count} (${pctValue}%)` };
            }
            return { key, label: toReadableLabel(key), value: dispatchDebugSummary[key] };
        });
    }, [dispatchDebugSummary]);
    const lateDetailsSummary = useMemo(
        () => (lateDetailsData?.summary && typeof lateDetailsData.summary === 'object'
            ? lateDetailsData.summary
            : null),
        [lateDetailsData]
    );
    const lateDetailsRows = useMemo(
        () => (Array.isArray(lateDetailsData?.lateOrders) ? lateDetailsData.lateOrders : []),
        [lateDetailsData]
    );
    const lateDetailsByClient = useMemo(() => {
        if (!lateDetailsRows.length) return [];
        const grouped = new Map();
        lateDetailsRows.forEach((row) => {
            const clientName = String(row?.clientName || 'Unknown Client').trim() || 'Unknown Client';
            const current = grouped.get(clientName) || {
                clientName,
                totalLateOrders: 0,
            };
            current.totalLateOrders += 1;
            grouped.set(clientName, current);
        });
        return Array.from(grouped.values())
            .sort((a, b) => {
                if (b.totalLateOrders !== a.totalLateOrders) return b.totalLateOrders - a.totalLateOrders;
                return a.clientName.localeCompare(b.clientName);
            });
    }, [lateDetailsRows]);
    useEffect(() => {
        if (!lateDetailsByClient.length) {
            if (selectedLateClientName) setSelectedLateClientName('');
            return;
        }
        const hasSelected = lateDetailsByClient.some((row) => row.clientName === selectedLateClientName);
        if (!hasSelected) {
            setSelectedLateClientName(lateDetailsByClient[0].clientName);
        }
    }, [lateDetailsByClient, selectedLateClientName]);
    const activeLateClientName = selectedLateClientName || lateDetailsByClient[0]?.clientName || '';
    const lateDetailsFilteredRows = useMemo(() => {
        if (!activeLateClientName) return lateDetailsRows;
        return lateDetailsRows.filter((row) => {
            const clientName = String(row?.clientName || 'Unknown Client').trim() || 'Unknown Client';
            return clientName === activeLateClientName;
        });
    }, [lateDetailsRows, activeLateClientName]);
    const paginatedLateDetailsRows = useMemo(() => {
        const start = lateDetailsPage * lateDetailsRowsPerPage;
        return lateDetailsFilteredRows.slice(start, start + lateDetailsRowsPerPage);
    }, [lateDetailsFilteredRows, lateDetailsPage, lateDetailsRowsPerPage]);
    useEffect(() => {
        const maxPage = Math.max(Math.ceil(lateDetailsFilteredRows.length / lateDetailsRowsPerPage) - 1, 0);
        if (lateDetailsPage > maxPage) {
            setLateDetailsPage(maxPage);
        }
    }, [lateDetailsFilteredRows.length, lateDetailsRowsPerPage, lateDetailsPage]);
    const lateDetailsColumns = useMemo(() => {
        if (!lateDetailsRows.length) return [];
        const preferredOrder = [
            'orderNumber',
            'clientName',
            'requiredDispatchDay',
            'lateReason',
            'isExpress',
            'createdAt',
            'dispatchedDate',
            'status',
            'shippingPriority',
            'shipping_carrier',
            'shipping_method',
            'cutoffTime',
            'standardWorkingDays',
        ];
        const allKeys = Array.from(
            lateDetailsRows.reduce((set, row) => {
                Object.keys(row || {}).forEach((key) => set.add(key));
                return set;
            }, new Set())
        );
        const ordered = preferredOrder.filter((key) => allKeys.includes(key));
        const extras = allKeys.filter((key) => !ordered.includes(key));
        return [...ordered, ...extras].filter((key) => key !== 'orderId');
    }, [lateDetailsRows]);
    const lateDetailsStickyLeftByColumn = useMemo(() => {
        const widths = { orderId: 110, orderNumber: 160 };
        let runningLeft = 0;
        return lateDetailsColumns.reduce((acc, column) => {
            if (!stickyDebugColumns.includes(column)) return acc;
            acc[column] = runningLeft;
            runningLeft += widths[column] || 140;
            return acc;
        }, {});
    }, [lateDetailsColumns]);

    return (
        <Layout>
            <Box sx={{ p: { xs: 1, sm: 2 }, backgroundColor: '#f5f5f5', minHeight: '100vh' }}>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', justifyContent: 'space-between', gap: 2, mb: 2 }}>
                    <Typography variant="h5" sx={{ fontWeight: 'bold' }}>
                        OTS DOT Report
                    </Typography>
                    <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                        <Button
                            variant="contained"
                            size="small"
                            onClick={handleExportPdf}
                            disabled={loading || pdfExporting}
                        >
                            {pdfExporting ? 'Exporting PDF...' : 'Export PDF'}
                        </Button>
                        <Button
                            variant="outlined"
                            size="small"
                            onClick={handleExportCsv}
                            disabled={loading || !monthlySummaryRows.length}
                        >
                            Export CSV
                        </Button>
                        <Button
                            variant="outlined"
                            size="small"
                            onClick={handleOpenLateDetails}
                            disabled={loading}
                        >
                            Not On-Time Details
                        </Button>
                        <Button
                            variant="outlined"
                            size="small"
                            onClick={handleOpenDispatchDebug}
                            disabled={!canOpenDispatchDebug || loading}
                        >
                            DOT Debug
                        </Button>
                    </Box>
                </Box>

                <Paper elevation={2} sx={{ p: 2, mb: 2 }}>
                    <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1.5, fontWeight: 600 }}>
                        Filters
                    </Typography>
                    <Grid container spacing={1.5} alignItems="flex-end">
                        <Grid item xs={12} sm={6} md={1.5} sx={{ minWidth: { md: 210 } }}>
                            <FormControl fullWidth size="small">
                                <InputLabel id="ots-dot-date-range-preset-label">Date Range</InputLabel>
                                <Select
                                    labelId="ots-dot-date-range-preset-label"
                                    value={filters.dateRangePreset}
                                    label="Date Range"
                                    onChange={(e) => {
                                        const preset = e.target.value;
                                        const range = getDateRangeFromPreset(preset);
                                        setFilters((prev) => ({ ...prev, dateRangePreset: preset, ...(range || {}) }));
                                    }}
                                >
                                    {dateRangePresetOptions.map((option) => (
                                        <MenuItem key={option.value} value={option.value}>
                                            {option.label}
                                        </MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                        </Grid>
                        <Grid item xs={6} sm={4} md={1.5} sx={{ minWidth: { md: 140 } }}>
                            <TextField
                                fullWidth
                                label="Start"
                                type="date"
                                size="small"
                                value={filters.startDate}
                                onChange={(e) => setFilters((prev) => ({ ...prev, startDate: e.target.value, dateRangePreset: 'CUSTOM' }))}
                                InputLabelProps={{ shrink: true }}
                                disabled={filters.dateRangePreset !== 'CUSTOM'}
                            />
                        </Grid>
                        <Grid item xs={6} sm={4} md={1.5} sx={{ minWidth: { md: 140 } }}>
                            <TextField
                                fullWidth
                                label="End"
                                type="date"
                                size="small"
                                value={filters.endDate}
                                onChange={(e) => setFilters((prev) => ({ ...prev, endDate: e.target.value, dateRangePreset: 'CUSTOM' }))}
                                InputLabelProps={{ shrink: true }}
                                disabled={filters.dateRangePreset !== 'CUSTOM'}
                            />
                        </Grid>
                        <Grid item xs={12} sm={6} md={2.5} sx={{ minWidth: { xs: '100%', sm: 360, md: 440 }, flexGrow: 1 }}>
                            <Box sx={{ display: 'flex', alignItems: 'flex-end', gap: 1 }}>
                                <Autocomplete
                                    multiple
                                    disableCloseOnSelect
                                    options={clients}
                                    value={selectedClientOptions}
                                    onChange={handleClientSelectionChange}
                                    isOptionEqualToValue={(option, value) => option.value === value.value}
                                    getOptionLabel={(option) => option.label || option.value || ''}
                                    limitTags={2}
                                    clearOnEscape
                                    sx={{ flex: 1, minWidth: 300 }}
                                    slotProps={{
                                        popper: {
                                            sx: {
                                                minWidth: { xs: 320, sm: 460 },
                                            },
                                        },
                                    }}
                                    ListboxProps={{ style: { maxHeight: 360 } }}
                                    renderOption={(props, option, { selected }) => (
                                        <li {...props}>
                                            <Checkbox size="small" checked={selected} sx={{ mr: 1, p: 0.5 }} />
                                            {option.label}
                                        </li>
                                    )}
                                    renderInput={(params) => (
                                        <TextField
                                            {...params}
                                            label="Client(s)"
                                            size="small"
                                            placeholder={selectedClientOptions.length === 0 ? 'All Clients' : 'Select'}
                                        />
                                    )}
                                />
                                <Box sx={{ display: 'flex', gap: 0.5, flexShrink: 0, pb: 0.75 }}>
                                    <Typography
                                        component="button"
                                        type="button"
                                        variant="caption"
                                        sx={{ cursor: clients.length ? 'pointer' : 'default', color: 'primary.main', border: 'none', background: 'none', p: 0, opacity: clients.length ? 1 : 0.5 }}
                                        onClick={handleSelectAllClients}
                                    >
                                        All
                                    </Typography>
                                    <Typography variant="caption" color="text.secondary">|</Typography>
                                    <Typography
                                        component="button"
                                        type="button"
                                        variant="caption"
                                        sx={{ cursor: selectedClientOptions.length ? 'pointer' : 'default', color: 'primary.main', border: 'none', background: 'none', p: 0, opacity: selectedClientOptions.length ? 1 : 0.5 }}
                                        onClick={handleClearAllClients}
                                    >
                                        Clear
                                    </Typography>
                                </Box>
                            </Box>
                        </Grid>
                        <Grid item xs={4} sm={3} md={1} sx={{ minWidth: { md: 120 } }}>
                            <TextField
                                fullWidth
                                label="Work Start"
                                type="time"
                                size="small"
                                value={filters.workingDayStart}
                                onChange={(e) => setFilters((prev) => ({ ...prev, workingDayStart: e.target.value }))}
                                InputLabelProps={{ shrink: true }}
                            />
                        </Grid>
                        <Grid item xs={4} sm={3} md={1} sx={{ minWidth: { md: 120 } }}>
                            <TextField
                                fullWidth
                                label="Work End"
                                type="time"
                                size="small"
                                value={filters.workingDayEnd}
                                onChange={(e) => setFilters((prev) => ({ ...prev, workingDayEnd: e.target.value }))}
                                InputLabelProps={{ shrink: true }}
                            />
                        </Grid>
                        <Grid item xs={4} sm={3} md={1} sx={{ minWidth: { md: 110 } }}>
                            <TextField
                                fullWidth
                                label="SLA (days)"
                                type="number"
                                size="small"
                                value={filters.standardSlaDays}
                                onChange={(e) => setFilters((prev) => ({ ...prev, standardSlaDays: e.target.value }))}
                                inputProps={{ min: 0, max: 30 }}
                            />
                        </Grid>
                        <Grid item xs={12} sm={6} md={2} sx={{ minWidth: { md: 220 } }}>
                            <FormControlLabel
                                control={(
                                    <Switch
                                        size="small"
                                        checked={Boolean(filters.includeOnHoldOrders)}
                                        onChange={(e) => setFilters((prev) => ({ ...prev, includeOnHoldOrders: e.target.checked }))}
                                    />
                                )}
                                label="Include issue-status orders"
                            />
                        </Grid>
                    </Grid>
                </Paper>

                {loading ? (
                    <Box sx={{ display: 'flex', justifyContent: 'center', mt: 6 }}>
                        <CircularProgress />
                    </Box>
                ) : (
                    <Box>
                        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1.5 }}>
                            Latest month: {latestPeriodLabel}. Averages: selected range ({filters.startDate} to {filters.endDate}).
                        </Typography>
                        <Box
                            sx={{
                                display: 'grid',
                                gridTemplateColumns: { xs: '1fr', sm: 'repeat(auto-fit, minmax(220px, 1fr))' },
                                gap: 2,
                                alignItems: 'stretch',
                                mb: 2,
                            }}
                        >
                            <Box sx={{ minWidth: 0, display: 'flex' }}>
                                <Paper elevation={1} sx={{ p: 2, borderLeft: 4, borderColor: 'success.main', borderRadius: 1, flex: 1 }}>
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                                        <CheckCircleOutlineIcon color="success" fontSize="small" />
                                        <Typography variant="caption" color="text.secondary" fontWeight={500}>
                                            On-Time Dispatched ({latestPeriodLabel})
                                        </Typography>
                                    </Box>
                                    <Typography variant="h5" fontWeight={600}>{`${latestOnTimeOrders.toLocaleString()} (${latestOnTimePct.toFixed(2)}%)`}</Typography>
                                </Paper>
                            </Box>
                            <Box sx={{ minWidth: 0, display: 'flex' }}>
                                <Paper elevation={1} sx={{ p: 2, borderLeft: 4, borderColor: 'error.main', borderRadius: 1, flex: 1 }}>
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                                        <CancelOutlinedIcon color="error" fontSize="small" />
                                        <Typography variant="caption" color="text.secondary" fontWeight={500}>
                                            Not On-Time Dispatched ({latestPeriodLabel})
                                        </Typography>
                                    </Box>
                                    <Typography variant="h5" fontWeight={600}>{`${latestNotOnTimeOrders.toLocaleString()} (${latestNotOnTimePct.toFixed(2)}%)`}</Typography>
                                </Paper>
                            </Box>
                            <Box sx={{ minWidth: 0, display: 'flex' }}>
                                <Paper elevation={1} sx={{ p: 2, borderLeft: 4, borderColor: 'info.main', borderRadius: 1, flex: 1 }}>
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                                        <SpeedIcon color="info" fontSize="small" />
                                        <Typography variant="caption" color="text.secondary" fontWeight={500}>
                                            Avg. Express On-Time %
                                        </Typography>
                                    </Box>
                                    <Typography variant="h6" fontWeight={600}>{avgMonthlyExpressPct != null ? `${avgMonthlyExpressPct.toFixed(2)}%` : '-'}</Typography>
                                </Paper>
                            </Box>
                            <Box sx={{ minWidth: 0, display: 'flex' }}>
                                <Paper elevation={1} sx={{ p: 2, borderLeft: 4, borderColor: 'primary.main', borderRadius: 1, flex: 1 }}>
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                                        <SpeedIcon color="primary" fontSize="small" />
                                        <Typography variant="caption" color="text.secondary" fontWeight={500}>
                                            Avg. Standard On-Time %
                                        </Typography>
                                    </Box>
                                    <Typography variant="h6" fontWeight={600}>{avgMonthlyStandardPct != null ? `${avgMonthlyStandardPct.toFixed(2)}%` : '-'}</Typography>
                                </Paper>
                            </Box>
                            <Box sx={{ minWidth: 0, display: 'flex' }}>
                                <Paper elevation={1} sx={{ p: 2, borderLeft: 4, borderColor: 'secondary.main', borderRadius: 1, flex: 1 }}>
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                                        <SpeedIcon color="secondary" fontSize="small" />
                                        <Typography variant="caption" color="text.secondary" fontWeight={500}>
                                            Avg. OTS DOT %
                                        </Typography>
                                    </Box>
                                    <Typography variant="h6" fontWeight={600}>{avgMonthlyOtsDotPct != null ? `${avgMonthlyOtsDotPct.toFixed(2)}%` : '-'}</Typography>
                                </Paper>
                            </Box>
                        </Box>

                        <Paper elevation={3} sx={{ p: 2, mb: 3 }} ref={exportChartRef}>
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1, gap: 1, flexWrap: 'wrap' }}>
                                <Typography variant="h6">
                                    OTS DOT% Trend
                                </Typography>
                            </Box>
                            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 2 }}>
                                Stacked bars show dispatched counts: On-Time vs Not On-Time. Top label shows monthly total orders.
                            </Typography>
                            <ResponsiveContainer width="100%" height={340}>
                                <BarChart data={trendChartData} margin={{ top: 36, right: 20, bottom: 5, left: 5 }}>
                                    <CartesianGrid strokeDasharray="3 3" />
                                    <XAxis dataKey="label" />
                                    <YAxis />
                                    <Tooltip
                                        labelFormatter={(label, payload) => {
                                            const point = payload?.[0]?.payload;
                                            const totalOrders = Number(point?.totalEligibleOrders || 0).toLocaleString();
                                            return `${label} - Total Orders: ${totalOrders}`;
                                        }}
                                        formatter={(value, name, entry) => {
                                            const payload = entry?.payload || {};
                                            if (name === 'On-Time Orders') {
                                                return [`${Number(value || 0).toLocaleString()} (${Number(payload.totalOnTimePct || 0).toFixed(2)}%)`, name];
                                            }
                                            if (name === 'Not On-Time Orders') {
                                                const latePct = Math.max(100 - Number(payload.totalOnTimePct || 0), 0);
                                                return [`${Number(value || 0).toLocaleString()} (${latePct.toFixed(2)}%)`, name];
                                            }
                                            return [Number(value || 0).toLocaleString(), name];
                                        }}
                                    />
                                    <Legend />
                                    <Bar
                                        dataKey="totalOnTimeOrders"
                                        stackId="total"
                                        fill="#1565c0"
                                        name="On-Time Orders"
                                        label={(props) => renderStackTopLabel(props, 'onTime')}
                                    />
                                    <Bar
                                        dataKey="totalLateOrders"
                                        stackId="total"
                                        fill="#ef6c00"
                                        name="Not On-Time Orders"
                                        label={(props) => renderStackTopLabel(props, 'late')}
                                    />
                                </BarChart>
                            </ResponsiveContainer>
                        </Paper>
                        <Paper elevation={3} sx={{ p: 2, mb: 3 }}>
                            <Typography variant="h6" sx={{ mb: 1 }}>
                                Monthly Dispatch Summary
                            </Typography>
                            <Box sx={{ overflowX: 'auto' }}>
                                <Table size="small">
                                    <TableHead>
                                        <TableRow>
                                            <TableCell sx={{ fontWeight: 700 }}>Month</TableCell>
                                            <TableCell sx={{ fontWeight: 700 }}>Total Orders</TableCell>
                                            <TableCell sx={{ fontWeight: 700 }}>No &amp; % On-Time Dispatch</TableCell>
                                            <TableCell sx={{ fontWeight: 700 }}>No &amp; % Not On-Time Dispatch</TableCell>
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {monthlySummaryRows.map((row) => (
                                            <TableRow key={row.month}>
                                                <TableCell>{row.month}</TableCell>
                                                <TableCell>{row.totalOrders.toLocaleString()}</TableCell>
                                                <TableCell>{`${row.onTimeOrders.toLocaleString()} (${row.onTimePct.toFixed(2)}%)`}</TableCell>
                                                <TableCell>{`${row.notOnTimeOrders.toLocaleString()} (${row.notOnTimePct.toFixed(2)}%)`}</TableCell>
                                            </TableRow>
                                        ))}
                                    </TableBody>
                                </Table>
                            </Box>
                        </Paper>
                    </Box>
                )}

                <Dialog
                    open={dispatchDebugModalOpen}
                    onClose={handleCloseDispatchDebug}
                    fullWidth
                    maxWidth="lg"
                    PaperProps={{ sx: { height: { xs: '92vh', md: '88vh' } } }}
                >
                    <DialogTitle>
                        DOT Debug - {selectedDebugClientName || 'Select client'} ({filters.endDate})
                    </DialogTitle>
                    <DialogContent
                        dividers
                        sx={{ display: 'flex', flexDirection: 'column', gap: 2, overflow: 'hidden' }}
                    >
                        {dispatchDebugLoading ? (
                            <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
                                <CircularProgress size={28} />
                            </Box>
                        ) : (
                            <>
                                {dispatchDebugSummary ? (
                                    <Box>
                                        <Typography variant="subtitle2" sx={{ mb: 1 }}>
                                            Summary
                                        </Typography>
                                        <Grid container spacing={1}>
                                            {dispatchDebugSummaryItems.map((item) => (
                                                <Grid item xs={12} sm={6} md={4} key={item.key}>
                                                    <Paper variant="outlined" sx={{ p: 1.25, borderRadius: 1.5, height: '100%' }}>
                                                        <Typography variant="caption" color="text.secondary">
                                                            {item.label}
                                                        </Typography>
                                                        <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
                                                            {String(item.value ?? '-')}
                                                        </Typography>
                                                    </Paper>
                                                </Grid>
                                            ))}
                                        </Grid>
                                    </Box>
                                ) : (
                                    <Typography variant="body2" color="text.secondary">
                                        No summary data returned.
                                    </Typography>
                                )}

                                <Typography variant="subtitle2" sx={{ mb: 1 }}>
                                    Contributing Orders ({contributingOrderCount})
                                </Typography>
                                {contributingOrderCount === 0 ? (
                                    <Box>
                                        <Typography variant="body2" color="text.secondary">
                                            No contributing orders returned for this client/date.
                                        </Typography>
                                        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.75 }}>
                                            Possible reasons: selected date/client has no required-dispatch orders, Shipping Priority filter is too restrictive,
                                            issue-status exclusion removed all rows, or cutoff/weekend/bank-holiday roll-forward moved orders to another working day.
                                        </Typography>
                                    </Box>
                                ) : (
                                    <Box
                                        sx={{
                                            flex: 1,
                                            minHeight: 0,
                                            overflow: 'auto',
                                            border: '1px solid',
                                            borderColor: 'divider',
                                            borderRadius: 1,
                                        }}
                                    >
                                        <Table size="small" stickyHeader>
                                            <TableHead>
                                                <TableRow>
                                                    {dispatchDebugColumns.map((column) => {
                                                        const isSticky = Object.prototype.hasOwnProperty.call(debugStickyLeftByColumn, column);
                                                        return (
                                                            <TableCell
                                                                key={column}
                                                                sx={{
                                                                    fontWeight: 600,
                                                                    minWidth: column === 'orderId' ? 110 : column === 'orderNumber' ? 160 : undefined,
                                                                    ...(isSticky
                                                                        ? {
                                                                            position: 'sticky',
                                                                            left: `${debugStickyLeftByColumn[column]}px`,
                                                                            zIndex: 4,
                                                                            backgroundColor: '#f5f5f5',
                                                                            boxShadow: '1px 0 0 rgba(224, 224, 224, 1)',
                                                                        }
                                                                        : {}),
                                                                }}
                                                            >
                                                                {toReadableLabel(column)}
                                                            </TableCell>
                                                        );
                                                    })}
                                                </TableRow>
                                            </TableHead>
                                            <TableBody>
                                                {dispatchDebugRows.map((row, idx) => (
                                                    <TableRow key={`${row.orderId || row.orderNo || 'row'}-${idx}`}>
                                                        {dispatchDebugColumns.map((column) => {
                                                            const rawValue = row?.[column];
                                                            const normalizedColumn = column.toLowerCase();
                                                            const value = normalizedColumn === 'cutofftime'
                                                                ? fmtTimeOnly(rawValue)
                                                                : ['requireddispatchday', 'required_dispatch_day'].includes(normalizedColumn)
                                                                    ? fmtDateOnly(rawValue)
                                                                    : isDateTimeColumn(column)
                                                                        ? fmtDateTime(rawValue)
                                                                        : rawValue;
                                                            const isSticky = Object.prototype.hasOwnProperty.call(debugStickyLeftByColumn, column);
                                                            return (
                                                                <TableCell
                                                                    key={`${column}-${idx}`}
                                                                    sx={{
                                                                        minWidth: column === 'orderId' ? 110 : column === 'orderNumber' ? 160 : undefined,
                                                                        ...(isSticky
                                                                            ? {
                                                                                position: 'sticky',
                                                                                left: `${debugStickyLeftByColumn[column]}px`,
                                                                                zIndex: 2,
                                                                                backgroundColor: '#fff',
                                                                                boxShadow: '1px 0 0 rgba(224, 224, 224, 1)',
                                                                            }
                                                                            : {}),
                                                                    }}
                                                                >
                                                                    {String(value ?? '-')}
                                                                </TableCell>
                                                            );
                                                        })}
                                                    </TableRow>
                                                ))}
                                            </TableBody>
                                        </Table>
                                    </Box>
                                )}
                            </>
                        )}
                    </DialogContent>
                    <DialogActions>
                        <Button onClick={handleCloseDispatchDebug}>Close</Button>
                    </DialogActions>
                </Dialog>
                <Dialog
                    open={lateDetailsModalOpen}
                    onClose={handleCloseLateDetails}
                    fullWidth
                    fullScreen={isMobile}
                    maxWidth="xl"
                    PaperProps={{ sx: { height: isMobile ? '100%' : { xs: '94vh', md: '92vh' } } }}
                >
                    <DialogTitle sx={{ fontSize: { xs: '1rem', sm: '1.25rem' }, py: { xs: 1.5, sm: 2 } }}>
                        Not On-Time Dispatch Details ({filters.startDate} to {filters.endDate})
                    </DialogTitle>
                    <DialogContent
                        dividers
                        sx={{ display: 'flex', flexDirection: 'column', gap: 1.25, overflow: 'hidden' }}
                    >
                        {lateDetailsLoading ? (
                            <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
                                <CircularProgress size={28} />
                            </Box>
                        ) : (
                            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.25, minHeight: 0, flex: 1 }}>
                                {lateDetailsSummary ? (
                                    <Grid container spacing={1}>
                                        <Grid item xs={6} sm={6} md={3}>
                                            <Paper variant="outlined" sx={{ p: 1.25, borderRadius: 1.5, height: '100%' }}>
                                                <Typography variant="caption" color="text.secondary">Total Late Orders</Typography>
                                                <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
                                                    {Number(lateDetailsSummary.totalLateOrders || 0).toLocaleString()}
                                                </Typography>
                                            </Paper>
                                        </Grid>
                                        <Grid item xs={6} sm={6} md={3}>
                                            <Paper variant="outlined" sx={{ p: 1.25, borderRadius: 1.5, height: '100%' }}>
                                                <Typography variant="caption" color="text.secondary">Express Late</Typography>
                                                <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
                                                    {Number(lateDetailsSummary.expressLateOrders || 0).toLocaleString()}
                                                </Typography>
                                            </Paper>
                                        </Grid>
                                        <Grid item xs={6} sm={6} md={3}>
                                            <Paper variant="outlined" sx={{ p: 1.25, borderRadius: 1.5, height: '100%' }}>
                                                <Typography variant="caption" color="text.secondary">Standard Late</Typography>
                                                <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
                                                    {Number(lateDetailsSummary.standardLateOrders || 0).toLocaleString()}
                                                </Typography>
                                            </Paper>
                                        </Grid>
                                        <Grid item xs={6} sm={6} md={3}>
                                            <Paper variant="outlined" sx={{ p: 1.25, borderRadius: 1.5, height: '100%' }}>
                                                <Typography variant="caption" color="text.secondary">Undispatched Late</Typography>
                                                <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
                                                    {Number(lateDetailsSummary.undispatchedLateOrders || 0).toLocaleString()}
                                                </Typography>
                                            </Paper>
                                        </Grid>
                                    </Grid>
                                ) : null}
                                {Boolean(lateDetailsSummary?.possiblyTruncated) && (
                                    <Typography variant="caption" color="warning.main">
                                        Results may be truncated at {Number(lateDetailsSummary.maxRows || 0).toLocaleString()} rows. Narrow filters for full detail.
                                    </Typography>
                                )}
                                {lateDetailsRows.length === 0 ? (
                                    <Typography variant="body2" color="text.secondary">
                                        No not-on-time orders found for selected filters and period.
                                    </Typography>
                                ) : (
                                    <Grid container spacing={1.25} sx={{ flex: 1, minHeight: 0 }}>
                                        <Grid item xs={12} md={3} sx={{ display: 'flex', flexDirection: 'column', minHeight: 0 }}>
                                            <Typography variant="subtitle2" sx={{ mb: 0.75 }}>
                                                Clients ({lateDetailsByClient.length})
                                            </Typography>
                                            {isMobile ? (
                                                <FormControl fullWidth size="small" sx={{ flexShrink: 0 }}>
                                                    <Select
                                                        value={activeLateClientName || ''}
                                                        onChange={(e) => {
                                                            setSelectedLateClientName(e.target.value);
                                                            setLateDetailsPage(0);
                                                        }}
                                                        displayEmpty
                                                    >
                                                        {lateDetailsByClient.map((row) => (
                                                            <MenuItem key={row.clientName} value={row.clientName}>
                                                                {row.clientName} ({row.totalLateOrders.toLocaleString()})
                                                            </MenuItem>
                                                        ))}
                                                    </Select>
                                                </FormControl>
                                            ) : (
                                                <Box
                                                    sx={{
                                                        flex: 1,
                                                        minHeight: 240,
                                                        overflow: 'auto',
                                                        border: '1px solid',
                                                        borderColor: 'divider',
                                                        borderRadius: 1,
                                                    }}
                                                >
                                                    <Table size="small" stickyHeader>
                                                        <TableHead>
                                                            <TableRow>
                                                                <TableCell sx={{ fontWeight: 600 }}>Client</TableCell>
                                                                <TableCell sx={{ fontWeight: 600 }}>Total Late</TableCell>
                                                            </TableRow>
                                                        </TableHead>
                                                        <TableBody>
                                                            {lateDetailsByClient.map((row) => {
                                                                const isSelected = row.clientName === activeLateClientName;
                                                                return (
                                                                    <TableRow
                                                                        key={`late-client-${row.clientName}`}
                                                                        hover
                                                                        onClick={() => {
                                                                            setSelectedLateClientName(row.clientName);
                                                                            setLateDetailsPage(0);
                                                                        }}
                                                                        selected={isSelected}
                                                                        sx={{ cursor: 'pointer' }}
                                                                    >
                                                                        <TableCell>{row.clientName}</TableCell>
                                                                        <TableCell>{row.totalLateOrders.toLocaleString()}</TableCell>
                                                                    </TableRow>
                                                                );
                                                            })}
                                                        </TableBody>
                                                    </Table>
                                                </Box>
                                            )}
                                        </Grid>
                                        <Grid item xs={12} md={9} sx={{ display: 'flex', flexDirection: 'column', minHeight: 0, overflow: 'hidden' }}>
                                            <Typography variant="subtitle2" sx={{ mb: 0.75, flexShrink: 0 }}>
                                                Late Orders - {activeLateClientName || 'All Clients'} ({lateDetailsFilteredRows.length})
                                            </Typography>
                                            <Box
                                                sx={{
                                                    flex: 1,
                                                    minHeight: { xs: 220, md: 350 },
                                                    maxHeight: { xs: '50vh', md: 490 },
                                                    overflowY: 'auto',
                                                    overflowX: 'auto',
                                                    border: '1px solid',
                                                    borderColor: 'divider',
                                                    borderRadius: 1,
                                                }}
                                            >
                                                <Table size="small" stickyHeader>
                                                    <TableHead>
                                                        <TableRow>
                                                            {lateDetailsColumns.map((column) => {
                                                                const isSticky = Object.prototype.hasOwnProperty.call(lateDetailsStickyLeftByColumn, column);
                                                                return (
                                                                    <TableCell
                                                                        key={column}
                                                                        sx={{
                                                                            fontWeight: 600,
                                                                            minWidth: column === 'orderNumber' ? 160 : ['requiredDispatchDay', 'createdAt', 'created_at', 'dispatchedDate', 'dispatched_date'].includes(column) ? 165 : undefined,
                                                                            whiteSpace: ['createdAt', 'created_at', 'dispatchedDate', 'dispatched_date'].includes(column) ? 'nowrap' : undefined,
                                                                            ...(isSticky
                                                                                ? {
                                                                                    position: 'sticky',
                                                                                    left: `${lateDetailsStickyLeftByColumn[column]}px`,
                                                                                    zIndex: 4,
                                                                                    backgroundColor: '#f5f5f5',
                                                                                    boxShadow: '1px 0 0 rgba(224, 224, 224, 1)',
                                                                                }
                                                                                : {}),
                                                                        }}
                                                                    >
                                                                        {toReadableLabel(column)}
                                                                    </TableCell>
                                                                );
                                                            })}
                                                        </TableRow>
                                                    </TableHead>
                                                    <TableBody>
                                                        {paginatedLateDetailsRows.map((row, idx) => {
                                                            const rowIdx = (lateDetailsPage * lateDetailsRowsPerPage) + idx;
                                                            return (
                                                                <TableRow key={`${row.orderId || row.orderNumber || 'late'}-${rowIdx}`}>
                                                                    {lateDetailsColumns.map((column) => {
                                                                        const rawValue = row?.[column];
                                                                        const normalizedColumn = column.toLowerCase();
                                                                        const value = normalizedColumn === 'cutofftime'
                                                                            ? fmtTimeOnly(rawValue)
                                                                            : ['requireddispatchday', 'required_dispatch_day'].includes(normalizedColumn)
                                                                                ? fmtDateOnly(rawValue)
                                                                                : isDateTimeColumn(column)
                                                                                    ? fmtDateTime(rawValue)
                                                                                    : rawValue;
                                                                        const isSticky = Object.prototype.hasOwnProperty.call(lateDetailsStickyLeftByColumn, column);
                                                                        return (
                                                                            <TableCell
                                                                                key={`${column}-${rowIdx}`}
                                                                                sx={{
                                                                                    minWidth: column === 'orderNumber' ? 160 : ['requiredDispatchDay', 'createdAt', 'created_at', 'dispatchedDate', 'dispatched_date'].includes(column) ? 165 : undefined,
                                                                                    whiteSpace: ['createdAt', 'created_at', 'dispatchedDate', 'dispatched_date'].includes(column) ? 'nowrap' : undefined,
                                                                                    ...(isSticky
                                                                                        ? {
                                                                                            position: 'sticky',
                                                                                            left: `${lateDetailsStickyLeftByColumn[column]}px`,
                                                                                            zIndex: 2,
                                                                                            backgroundColor: '#fff',
                                                                                            boxShadow: '1px 0 0 rgba(224, 224, 224, 1)',
                                                                                        }
                                                                                        : {}),
                                                                                }}
                                                                            >
                                                                                {String(value ?? '-')}
                                                                            </TableCell>
                                                                        );
                                                                    })}
                                                                </TableRow>
                                                            );
                                                        })}
                                                    </TableBody>
                                                </Table>
                                            </Box>
                                            <TablePagination
                                                component="div"
                                                count={lateDetailsFilteredRows.length}
                                                page={lateDetailsPage}
                                                onPageChange={handleLateDetailsPageChange}
                                                rowsPerPage={lateDetailsRowsPerPage}
                                                onRowsPerPageChange={handleLateDetailsRowsPerPageChange}
                                                rowsPerPageOptions={lateDetailsRowsPerPageOptions}
                                                showFirstButton
                                                showLastButton
                                                labelRowsPerPage="Rows:"
                                                sx={{
                                                    borderTop: '1px solid',
                                                    borderColor: 'divider',
                                                    backgroundColor: 'background.paper',
                                                    flexShrink: 0,
                                                    '.MuiTablePagination-toolbar': { minHeight: 44, px: 1 },
                                                }}
                                            />
                                        </Grid>
                                    </Grid>
                                )}
                            </Box>
                        )}
                    </DialogContent>
                    <DialogActions>
                        <Button onClick={handleCloseLateDetails}>Close</Button>
                    </DialogActions>
                </Dialog>
            </Box>
        </Layout>
    );
};

export default OtsDotReport;
